<template>
  <div class="app" :class="{ 'is-plain': isPlain }">
    <div class="loading-screen" v-if="isLoading && !isPlain">
      <div class="loading-content">
        <div class="loading-icon">
          <i class="fas fa-music pulse"></i>
          <div class="loading-wave">
            <div class="wave-bar" v-for="n in 5" :key="n"></div>
          </div>
        </div>
        <span class="loading-text">Loading MP3 Converter</span>
      </div>
    </div>
    <NavBar v-if="!isPlain" />
    <router-view v-if="isPlain || !isLoading" />
    <AppFooter v-if="!isPlain" />
    <AppToast />
  </div>
</template>

<script setup>
import '@/assets/styles/global.css'
import { ref, onMounted, computed, watch } from 'vue'
import { useRoute } from 'vue-router'
import NavBar from '@/components/layout/NavBar.vue'
import AppFooter from '@/components/layout/AppFooter.vue'
import AppToast from '@/components/common/AppToast.vue'

const isLoading = ref(true)
const route = useRoute()
const isPlain = computed(() => !!route.meta.plain)

watch(
  isPlain,
  (val) => {
    if (typeof document === 'undefined') return
    document.body.classList.toggle('is-plain', val)
  },
  { immediate: true }
)

const observeElements = () => {
  const observer = new IntersectionObserver((entries) => {
    entries.forEach(entry => {
      if (entry.isIntersecting) {
        entry.target.classList.add('animate-in')
      }
    })
  }, { threshold: 0.1 })
  
  document.querySelectorAll('.section-container').forEach(el => {
    observer.observe(el)
  })
}

onMounted(() => {
  setTimeout(() => {
    isLoading.value = false
  }, 2000)
  observeElements()
})
</script>

<style scoped>
.app {
  min-height: 100vh;
}

main {
  padding-top: 4rem;
}

.loading-screen {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: var(--background-primary);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  animation: fadeOut 0.5s ease forwards;
  animation-delay: 1.5s;
}

.loading-content {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 2rem;
}

.loading-icon {
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 1rem;
}

.loading-icon i {
  font-size: 3rem;
  color: #00c6ff;
  animation: pulse 1s ease infinite;
}

.loading-wave {
  display: flex;
  align-items: flex-end;
  gap: 3px;
  height: 20px;
}

.wave-bar {
  width: 3px;
  background: var(--primary-gradient);
  border-radius: 1px;
  animation: wave 1s ease infinite;
}

.wave-bar:nth-child(1) { animation-delay: 0.0s; }
.wave-bar:nth-child(2) { animation-delay: 0.1s; }
.wave-bar:nth-child(3) { animation-delay: 0.2s; }
.wave-bar:nth-child(4) { animation-delay: 0.3s; }
.wave-bar:nth-child(5) { animation-delay: 0.4s; }

.loading-text {
  color: var(--text-secondary);
  font-size: 1.1rem;
  letter-spacing: 1px;
}

@keyframes pulse {
  0% { transform: scale(1); opacity: 0.5; }
  50% { transform: scale(1.1); opacity: 1; }
  100% { transform: scale(1); opacity: 0.5; }
}

@keyframes wave {
  0% { height: 5px; }
  50% { height: 20px; }
  100% { height: 5px; }
}

@keyframes fadeOut {
  from { opacity: 1; visibility: visible; }
  to { opacity: 0; visibility: hidden; }
}

.section-container {
  opacity: 0;
  transform: translateY(30px);
  transition: all 0.8s ease;
}

.section-container.animate-in {
  opacity: 1;
  transform: translateY(0);
}
</style>
